import classNames from "classnames";
import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";

import { ButtonContainer } from "components/button-container/ButtonContainer";
import Button from "components/button/Button";
import ErrorMessage from "components/error-message/ErrorMessage";
import IconButton from "components/icon-button/IconButton";
import LeftArrow from "components/icons/LeftArrow";
import { subpageContext } from "components/layout/ApplicationLayout";
import style from "components/layout/subpage-layout/subpage-layout.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Heading from "components/typography/heading/Heading";
import { resolveColor } from "utils/commonFunctions";

import testIds from "testIds.json";

interface Props {
    visible: boolean;
    title: string;
    buttons?: React.ReactNode;
    children?: React.ReactNode;
    loading?: boolean;
    primaryButtonRequired?: boolean;
    handleRefresh?: (value: boolean) => void;
    onClose?: () => void;
    footerBanner?: React.ReactNode;
    loadingIndicator?: React.ReactNode;
}

export const SubpageLayout = (props: Props) => {
    const { t } = useTranslation();
    const context = React.useContext(subpageContext);

    const handleCloseClick = () => {
        if (props.onClose) {
            props.onClose();
        } else {
            context?.setSubpage({
                content: undefined,
            });
        }
    };

    return (
        <div className={style.subpageLayout}>
            {props.loading ? (
                <LoadingIndicator isView />
            ) : (
                <>
                    <div className={classNames(style.layoutPadding, style.subpageContainer)}>
                        <div className={style.subpageHeader}>
                            <IconButton
                                onClick={handleCloseClick}
                                testId={testIds.common.subpageView.backToPrimaryViewButton}
                            >
                                <LeftArrow color={resolveColor("--theme-icon-fill-default")} />
                            </IconButton>
                            <Heading tag="h1" variant="H1">
                                {props.title}
                            </Heading>
                        </div>
                        <div className={style.subpageContent}>
                            <ErrorBoundary FallbackComponent={ErrorMessage}>{props.children}</ErrorBoundary>
                        </div>
                    </div>
                    {props.footerBanner}
                    <div className={classNames(style.layoutPadding, style.subpageFooter)}>
                        <ButtonContainer alignment="LEFT" noSpacingTop={true}>
                            {props.buttons}
                            <Button
                                variant={props.primaryButtonRequired ? "PRIMARY" : "SECONDARY"}
                                type={"button"}
                                onClick={handleCloseClick}
                                testId={testIds.common.subpageView.cancelButton}
                            >
                                {props.primaryButtonRequired ? t("Common.ok") : t("Common.cancel")}
                            </Button>
                        </ButtonContainer>
                        {props.loadingIndicator && (
                            <div className={style.footerLoadingIndicator}>{props.loadingIndicator}</div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
